(ns ui.gallery.grid
  "The virtualized grid of the Replicant gallery. It only renders the
   entries in and around the viewport, while a CSS grid with the full
   row count keeps the scrollbar honest. The scroll position of the
   container is turned into offsets (see `ui.gallery.layout`), which
   decide what is rendered, and after a short pause in scrolling into a
   `:gallery/scrolled` action, which loads the page that covers the
   viewport."
  (:require [goog.functions :as functions]
            [ui.gallery.card :as card]
            [ui.gallery.layout :as layout]))

(def grid-id
  "gallery-grid")

(def scroll-debounce-ms
  ;; only when the user stops scrolling for this long, the grid asks
  ;; for the page that covers the viewport. This avoids loading pages
  ;; that are scrolled past quickly:
  100)

(defn node-offsets
  [node]
  (layout/calc-offsets (.-scrollTop node)
                       (.-clientWidth node)
                       (.-clientHeight node)))

(defn initial-offsets
  "Estimates the offsets before the grid is mounted from the window
   size. The grid is smaller than the window, so this over-estimates,
   which only loads a few entries too many."
  []
  (layout/calc-offsets 0
                       js/window.innerWidth
                       js/window.innerHeight))

(defn on-mount
  "Attaches the scroll listener and a resize observer (a wider or
   zoomed-out viewport shows more entries per row, which may exceed
   the loaded page) to the mounted grid container."
  [dispatch!]
  (fn [{:keys [replicant/node replicant/remember]}]
    (let [load! (functions/debounce
                  (fn []
                    (dispatch! {:actions [[:gallery/scrolled (node-offsets node)]]}))
                  scroll-debounce-ms)
          on-scroll (fn [_e]
                      (dispatch! {:actions [[:gallery/set-scroll (node-offsets node)]]})
                      (load!))
          observer (js/ResizeObserver. (fn [_entries]
                                         (on-scroll nil)))]
      (.addEventListener node "scroll" on-scroll #js {:passive true})
      (.observe observer node)
      (dispatch! {:actions [[:gallery/set-scroll (node-offsets node)]]})
      (remember {:on-scroll on-scroll
                 :observer observer}))))

(defn on-unmount
  [{:keys [replicant/node replicant/memory]}]
  (when memory
    (.removeEventListener node "scroll" (:on-scroll memory))
    (.disconnect (:observer memory))))

(defn cell
  [gallery entries per-row n]
  [:div {:replicant/key n
         :style {:grid-area (layout/grid-area per-row n)
                 :margin-right (str layout/margin "px")
                 :margin-bottom (str layout/margin "px")
                 :width (str layout/width "px")
                 :height (str layout/height "px")}}
   (when-let [entry (get entries n)]
     (card/card gallery entry))])

(defn grid
  "The grid for the gallery `result` (the value of the current
   `:query/gallery`)."
  [state result]
  (let [gallery (:gallery state)
        offsets (or (:scroll gallery)
                    (initial-offsets))
        per-row (:per-row offsets)
        entry-count (or (:gallery/entry-count result) 0)
        entries (:gallery/entries result)
        rows (layout/total-rows per-row entry-count)]
    [:div.grow.overflow-y-scroll
     {:id grid-id
      ;; The key makes Replicant create a fresh element when the grid
      ;; replaces the loading placeholder or the empty card (both divs
      ;; at the same position), otherwise it would reuse their element
      ;; and never call `on-mount`: no scroll listener, no resize
      ;; observer, and the columns would stay at the window-based
      ;; estimate.
      :replicant/key ::grid
      :replicant/on-mount (on-mount (:replicant/dispatch! state))
      :replicant/on-unmount on-unmount}
     [:div.justify-center
      {:style {:display "grid"
               :height (str (* layout/row-height rows) "px")
               :grid-template-columns (str "repeat(" per-row "," layout/column-width "px)")
               :grid-template-rows (str "repeat(" (max 1 rows) "," layout/row-height "px)")}}
      (map (partial cell gallery entries per-row)
           (layout/visible-range offsets entry-count))]]))
